import React, { useState, useEffect } from 'react';
import { Bell, TestTube, CheckCircle, AlertCircle, Trophy, Calendar, Target, Zap } from 'lucide-react';
import { Button } from '../ui/Button';
import { useNotificationStore } from '../../store/notificationStore';
import { NotificationType } from '../../types';
import toast from 'react-hot-toast';

interface TestNotification {
  type: NotificationType;
  title: string;
  body: string;
  icon: React.ReactNode;
  color: string;
}

const testNotifications: TestNotification[] = [
  {
    type: NotificationType.HabitReminder,
    title: '🏃‍♂️ Time for your habit!',
    body: 'Don\'t forget to complete your daily run. Keep your streak alive!',
    icon: <Bell className="w-5 h-5" />,
    color: 'bg-blue-500'
  },
  {
    type: NotificationType.StreakMilestone,
    title: '🔥 Streak Milestone!',
    body: 'Congratulations! You\'ve reached a 7-day streak on your morning meditation habit!',
    icon: <Trophy className="w-5 h-5" />,
    color: 'bg-orange-500'
  },
  {
    type: NotificationType.Achievement,
    title: '🏆 Achievement Unlocked!',
    body: 'Perfect Week! You\'ve completed all your habits for 7 consecutive days.',
    icon: <Target className="w-5 h-5" />,
    color: 'bg-green-500'
  },
  {
    type: NotificationType.MissedHabit,
    title: '⚠️ Habit Missed',
    body: 'You missed your reading habit yesterday. Don\'t let your streak break!',
    icon: <AlertCircle className="w-5 h-5" />,
    color: 'bg-red-500'
  },
  {
    type: NotificationType.StreakEndanger,
    title: '🚨 Streak at Risk!',
    body: 'Your 14-day workout streak is at risk. Complete it before midnight!',
    icon: <Zap className="w-5 h-5" />,
    color: 'bg-yellow-500'
  },
  {
    type: NotificationType.WeeklyReport,
    title: '📊 Weekly Report',
    body: 'Your weekly habit report is ready! You completed 85% of your habits this week.',
    icon: <Calendar className="w-5 h-5" />,
    color: 'bg-purple-500'
  },
  {
    type: NotificationType.Motivation,
    title: '💪 Stay Motivated!',
    body: 'Remember: Small daily improvements lead to stunning long-term results!',
    icon: <CheckCircle className="w-5 h-5" />,
    color: 'bg-indigo-500'
  }
];

export const NotificationTester: React.FC = () => {
  const {
    browserPushSupported,
    browserPushPermission,
    requestBrowserPushPermission,
    sendTestNotification,
    checkBrowserPushSupport,
    isLoading
  } = useNotificationStore();

  const [testingType, setTestingType] = useState<NotificationType | null>(null);

  useEffect(() => {
    checkBrowserPushSupport();
  }, [checkBrowserPushSupport]);

  const handleRequestPermission = async () => {
    const granted = await requestBrowserPushPermission();
    if (granted) {
      toast.success('Browser notifications enabled! You can now test notifications.');
    } else {
      toast.error('Browser notifications permission denied. Please enable in browser settings.');
    }
  };

  const handleTestNotification = async (testNotif: TestNotification) => {
    setTestingType(testNotif.type);
    
    try {
      // First try the API test notification
      await sendTestNotification(testNotif.type);
      
      // Also show a direct browser notification for immediate feedback
      if (browserPushPermission === 'granted') {
        showDirectBrowserNotification(testNotif);
      }
      
      toast.success(`${testNotif.title} notification sent!`);
    } catch (error) {
      toast.error('Failed to send test notification');
      console.error('Test notification error:', error);
    } finally {
      setTestingType(null);
    }
  };

  const showDirectBrowserNotification = (testNotif: TestNotification) => {
    // Extra safety checks for iOS Safari compatibility
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('Browser notifications not supported');
      return;
    }
    
    if (Notification.permission !== 'granted') {
      console.warn('Browser notification permission not granted');
      return;
    }

    try {
      const notification = new Notification(testNotif.title, {
        body: testNotif.body,
        icon: '/logo192.png', // You can replace with your app icon
        badge: '/logo192.png',
        tag: `test-${testNotif.type}`,
        requireInteraction: false,
        silent: false
      });

      // Auto close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Handle click
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.warn('Failed to create browser notification:', error);
    }
  };

  const getPermissionStatus = () => {
    if (!browserPushSupported) {
      return {
        text: 'Browser notifications not supported',
        color: 'text-gray-500',
        bgColor: 'bg-gray-100 dark:bg-gray-800'
      };
    }

    switch (browserPushPermission) {
      case 'granted':
        return {
          text: 'Browser notifications enabled',
          color: 'text-green-700 dark:text-green-300',
          bgColor: 'bg-green-100 dark:bg-green-900/20'
        };
      case 'denied':
        return {
          text: 'Browser notifications blocked',
          color: 'text-red-700 dark:text-red-300',
          bgColor: 'bg-red-100 dark:bg-red-900/20'
        };
      default:
        return {
          text: 'Browser notifications permission needed',
          color: 'text-yellow-700 dark:text-yellow-300',
          bgColor: 'bg-yellow-100 dark:bg-yellow-900/20'
        };
    }
  };

  const permissionStatus = getPermissionStatus();

  return (
    <div className="space-y-6 px-4 md:px-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <TestTube className="w-6 h-6" />
            Notification Tester
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Test different types of browser notifications to see how they work
          </p>
        </div>
      </div>

      {/* Permission Status */}
      <div className={`card p-4 ${permissionStatus.bgColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Bell className={`w-5 h-5 ${permissionStatus.color}`} />
            <span className={`font-medium ${permissionStatus.color}`}>
              {permissionStatus.text}
            </span>
          </div>
          
          {browserPushSupported && browserPushPermission !== 'granted' && (
            <Button
              onClick={handleRequestPermission}
              disabled={isLoading}
              size="sm"
              variant="primary"
            >
              {browserPushPermission === 'denied' ? 'Check Settings' : 'Enable Notifications'}
            </Button>
          )}
        </div>
        
        {browserPushPermission === 'denied' && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            To enable notifications: Go to your browser settings → Site settings → Notifications → Allow
          </p>
        )}
              </div>

      {/* Test Notifications Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {testNotifications.map((testNotif) => (
          <div key={testNotif.type} className="card p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-lg ${testNotif.color} text-white flex-shrink-0`}>
                {testNotif.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                  {testNotif.title}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                  {testNotif.body}
                </p>
                <Button
                  onClick={() => handleTestNotification(testNotif)}
                  disabled={!browserPushSupported || isLoading || testingType === testNotif.type}
                  size="sm"
                  variant="outline"
                  className="mt-3 w-full"
                >
                  {testingType === testNotif.type ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    'Test Notification'
                  )}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Instructions */}
      <div className="card p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
          How to Test Browser Notifications:
        </h3>
        <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
          <li>First, enable browser notifications by clicking "Enable Notifications" above</li>
          <li>Click "Test Notification" on any notification type card</li>
          <li>You should see the notification appear in your browser/system</li>
          <li>Check your browser's notification center if you miss them</li>
          <li>Try different notification types to see various styles and content</li>
        </ol>
        
        <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-800/30 rounded-lg">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>Note:</strong> These are test notifications. In the real app, notifications are triggered 
            automatically based on your habit completion patterns, reminders, and achievements.
          </p>
        </div>
      </div>
    </div>
  );
}; 